import { useEffect, useLayoutEffect, useState } from "react";
import styles from "./poster-editor-toolbar.module.css";
import { ToolboxRouteSource } from "./toolboxes/pre-made-toolboxes/route-source/toolbox-route-source";
import { ToolboxMapStyle } from "./toolboxes/pre-made-toolboxes/map-style/toolbox-map-style";
import { ToolboxPosterSize } from "./toolboxes/pre-made-toolboxes/poster-size/toolbox-poster-size";
import { ToolboxLineStyle } from "./toolboxes/pre-made-toolboxes/line-style/toolbox-line-style";
import { ToolboxBorderStyle } from "./toolboxes/pre-made-toolboxes/border-style/border-style";
import { ToolboxMapLabelStyle } from "./toolboxes/pre-made-toolboxes/labels/toolbox-label-style";
import { ToolboxStaticMapLabelStyle } from "./toolboxes/pre-made-toolboxes/labels/toolbox-static-label-style";
import { ToolboxPointStyle } from "./toolboxes/pre-made-toolboxes/point-style/toolbox-point-style";
import { UIIconName } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-type";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { filterClasses } from "PosterEditor/utils/filter-classes";
import { useEditorUI, EditorUIState } from "PosterEditor/use-editor-ui";
import { ToolboxUnselectedElement } from "PosterEditor/map/overlay/point-editor/toolbar-unselected-element";
import { ToolboxAllLabels } from "./toolboxes/pre-made-toolboxes/labels/toolbox-all-labels";
import { ToolboxAllBackdrops } from "./toolboxes/pre-made-toolboxes/backdrops/toolbox-all-backdrops";
import { ToolboxBackdrop } from "./toolboxes/pre-made-toolboxes/backdrops/toolbox-backdrop";
import { useAccount } from "BEDROCK/account/use-account";
import { TemplateEditing } from "./toolboxes/pre-made-toolboxes/template-editing/template-editing";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { DraftOptions } from "./toolboxes/pre-made-toolboxes/draft-options/draft-options";
import { AnimatePresence } from "motion/react"; // export type ToolboxType =
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { PosterProject } from "PosterEditor/model/poster-project";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
//   | "templateEditing"
//   | "pointStyle"
//   | "lineStyle"
//   | "mapSize"
//   | "gpsSources"
//   | "mapStyle"
//   | "legend"
//   | "borderStyle";

interface PosterEditorToolbarProps {
  onClickSave: () => void;
}

export type ToolboxItemType =
  | "draftOptions"
  | "templateEditing"
  | "pointStyle"
  | "labelStyle"
  | "lineStyle"
  | "mapSize"
  | "gpsSources"
  | "mapStyle"
  | "borderStyle"
  | "backdrops";

const iconForToolboxType = (type: ToolboxItemType): UIIconName => {
  switch (type) {
    case "draftOptions":
      return "PENCIL";
    case "templateEditing":
      return "TEMPLATES";
    case "pointStyle":
      return "POINT-STYLE";
    case "lineStyle":
      return "LINE-STYLE";
    case "mapSize":
      return "SIZE";
    case "gpsSources":
      return "GPS";
    case "mapStyle":
      return "MAP";
    case "borderStyle":
      return "BORDER";
    case "labelStyle":
      return "STATIC-LABELS";
    case "backdrops":
      return "BACKDROP";
  }
};

const descriptionForToolboxType = (type: ToolboxItemType): string => {
  switch (type) {
    case "draftOptions":
      return "Draft";
    case "templateEditing":
      return "Edit the template";
    case "pointStyle":
      return "Style points of your route with icons and colors";
    case "lineStyle":
      return "Color your route line and adjust it's thickness";
    case "mapSize":
      return "Choose a size for your map";
    case "gpsSources":
      return "Upload a GPS file to create a route";
    case "mapStyle":
      return "Choose a color theme for your map";
    case "borderStyle":
      return "Change the border width and color";
    case "labelStyle":
      return "Customise labels on your map. Click on your map to add labels";
    case "backdrops":
      return "Add backdrops to provide frames around labels";
  }
};

export const PosterEditorToolbar = (props: PosterEditorToolbarProps) => {
  const { editorUIState, setEditingElement } = useEditorUI();
  const { project, template, isTemplateEditing } = usePosterProject();
  const { account } = useAccount();
  const [itemUnderCursor, setItemUnderCursor] = useState<ToolboxItemType>();
  const [test, setTest] = useState<boolean>(false);

  const [selectedType, setSelectedType] = useState<ToolboxItemType | undefined>(
    project.isTemplateDraft
      ? "draftOptions"
      : template !== undefined
      ? "templateEditing"
      : undefined
  );
  let toolbarItems: ToolboxItemType[];
  if (project.isTemplateDraft) {
    toolbarItems = ["draftOptions"];
  } else {
    toolbarItems = [
      "gpsSources",
      "mapStyle",
      "mapSize",
      "lineStyle",
      "borderStyle",
      "pointStyle",
      "labelStyle",
      "backdrops",
    ];
    // if account permissions is "admin" insert "templateEditing" at the beginning of the array
    if (isTemplateEditing) {
      toolbarItems.unshift("templateEditing");
    }

    if (!isTemplateEditing) {
      if (project.editingPermissions.gpsSource === false) {
        toolbarItems = toolbarItems.filter((item) => item !== "gpsSources");
      }
      if (project.editingPermissions.mapStyle === false) {
        toolbarItems = toolbarItems.filter((item) => item !== "mapStyle");
      }
      if (project.editingPermissions.posterSize === false) {
        toolbarItems = toolbarItems.filter((item) => item !== "mapSize");
      }
      // check if all items in project.editingPermissions.line are false and filter out `lineStyle`
      if (
        Object.values(project.editingPermissions.line).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "lineStyle");
      }
      //likewise for point
      if (
        Object.values(project.editingPermissions.point).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "pointStyle");
      }
      //likewise for border
      if (
        Object.values(project.editingPermissions.border).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "borderStyle");
      }
      //likewise for mapLabel
      if (
        Object.values(project.editingPermissions.mapLabel).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "labelStyle");
      }
      //likewise for staticLabel
      if (
        Object.values(project.editingPermissions.staticLabel).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "labelStyle");
      }
      //likewise for backdrop
      if (
        Object.values(project.editingPermissions.backdrop).every(
          (value) => value === false
        )
      ) {
        toolbarItems = toolbarItems.filter((item) => item !== "backdrops");
      }
    }
  }

  useEffect(() => {
    if (!project.isTemplateDraft && !template) {
      setSelectedType("gpsSources");
    }
  }, [project.isTemplateDraft]);

  useLayoutEffect(() => {
    const element = editorUIState.editingElement;
    if (element?.type === "point") {
      setSelectedType("pointStyle");
    } else if (
      element?.type === "mapLabel" ||
      element?.type === "staticLabel"
    ) {
      setSelectedType("labelStyle");
    } else if (element?.type === "backdrop") {
      setSelectedType("backdrops");
    }
  }, [editorUIState.editingElement]);

  const onClickItem = (item: ToolboxItemType) => {
    setEditingElement(undefined);
    setSelectedType(item);
  };

  const selectedColor = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-selected");

  const textColorPrimary = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-text-primary");

  const backgroundColor = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--color-background");

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div
          className={filterClasses(
            styles.toolbarItemStack,
            project.isTemplateDraft ? styles.toolbarItemStackHidden : undefined
          )}
        >
          {toolbarItems.map((item, index) => {
            return (
              <div
                key={index}
                onMouseEnter={() => setItemUnderCursor(item)}
                onMouseLeave={() => setItemUnderCursor(undefined)}
                className={filterClasses(
                  styles.toolbarItem,
                  selectedType === item
                    ? undefined
                    : styles.toolbarItemHoverable
                )}
              >
                <button onClick={() => onClickItem(item)}>
                  <div className={styles.icon}>
                    <UIIcon
                      iconName={iconForToolboxType(item)}
                      variant={selectedType === item ? "SELECTED" : "REGULAR"}
                      size={27}
                    />
                  </div>
                  <div className={styles.itemTextContainer}>
                    <h1 className={styles.itemTitle}>
                      {titleForComponent(project, item)}
                    </h1>
                    <p className={styles.itemDescription}>
                      {descriptionForToolboxType(item)}
                    </p>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
        <PresentedContent type={selectedType} project={project} />
      </div>
      {!isTemplateEditing && (
        <div className={styles.saveCTAContainer}>
          <CTAButton
            title="Save and continue"
            variant="PRIMARY"
            className={styles.cta}
            onClick={props.onClickSave}
            disabled={project.isTemplateDraft}
          />
        </div>
      )}
    </div>
  );
};

const titleForComponent = (
  project: PosterProject,
  type?: ToolboxItemType
): string | undefined => {
  switch (type) {
    case "draftOptions":
      return project.name;
    case "templateEditing":
      return "Template";
    case "pointStyle":
      return "Points";
    case "lineStyle":
      return "Line Style";
    case "mapSize":
      return "Map Size";
    case "gpsSources":
      return "GPS Source.";
    case "mapStyle":
      return "Map Style";
    case "borderStyle":
      return "Border Style";
    case "labelStyle":
      return "LABELS";
    case "backdrops":
      return "BACKROPS";
    default:
      return undefined;
  }
};

interface PresentedContentProps {
  type?: ToolboxItemType;
  project: PosterProject;
}
const PresentedContent = (props: PresentedContentProps) => {
  const { editorUIState } = useEditorUI();
  const component = componentForType(editorUIState, props.type);

  let title = titleForComponent(props.project, props.type);

  return (
    <div className={styles.presentedContent}>
      {title && <div className={styles.title}>{title}</div>}
      {component}
    </div>
  );
};

const componentForType = (
  editorState: EditorUIState,
  type?: ToolboxItemType
) => {
  switch (type) {
    case "draftOptions":
      return <DraftOptions />;
    case "templateEditing":
      return <TemplateEditing />;
    case "pointStyle":
      const point = editorState.editingElement?.styledPoint;
      if (!point) {
        return (
          <ToolboxUnselectedElement title="Select a point on your route to customise it's style" />
        );
      }
      return <ToolboxPointStyle point={point} />;
    case "labelStyle":
      const label = editorState.editingElement?.mapLabel;
      if (label) {
        return <ToolboxMapLabelStyle label={label} />;
      }
      const staticLabel = editorState.editingElement?.staticLabel;
      if (staticLabel) {
        return <ToolboxStaticMapLabelStyle label={staticLabel} />;
      }
      return <ToolboxAllLabels />;
    case "backdrops":
      const backdrop = editorState.editingElement?.backdrop;
      if (backdrop) {
        return <ToolboxBackdrop backdrop={backdrop} />;
      }
      return <ToolboxAllBackdrops />;
    case "lineStyle":
      return <ToolboxLineStyle showTitleBar={false} collapsible={false} />;
    case "mapSize":
      return <ToolboxPosterSize showTitleBar={false} collapsible={false} />;
    case "gpsSources":
      return <ToolboxRouteSource showTitleBar={false} collapsible={false} />;
    case "mapStyle":
      return <ToolboxMapStyle showTitleBar={false} collapsible={false} />;
    case "borderStyle":
      return <ToolboxBorderStyle showTitleBar={false} collapsible={false} />;
    default:
      return <div>Nothing Selected</div>;
  }
};
